import { repairImageKitUrl } from '@dupe/common/constants'
import { ArrowRight, ImageOff } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'
import { cn } from '~/src/lib/utils'
import { toBuyLinkHref } from '~/src/lib/withRedirectClickSource'
import { useHubAnalytics } from '../HubAnalyticsContext'
import { renderRichText } from './richText'
import type {
  HubProductRef,
  HubReportRef,
  HubSectionComponentProps,
  HubTopPicksPick,
  HubTopPicksSection,
} from './types'

const PRICE_FORMATTERS = new Map<string, Intl.NumberFormat>()

function formatPrice(
  price: number | null | undefined,
  currency: string | null | undefined
): string {
  if (price == null) return ''
  const hasCents = !Number.isInteger(price)
  const currencyCode = currency ?? 'USD'
  const formatterKey = `${currencyCode}:${hasCents ? 'cents' : 'whole'}`
  try {
    const formatter =
      PRICE_FORMATTERS.get(formatterKey) ??
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
      })
    PRICE_FORMATTERS.set(formatterKey, formatter)
    return formatter.format(price)
  } catch {
    return hasCents ? `$${price.toFixed(2)}` : `$${price}`
  }
}

function PickThumb({ product }: { product: HubProductRef }) {
  const [errored, setErrored] = useState(false)

  if (!product.image || errored) {
    return (
      <div className='flex h-full w-full items-center justify-center bg-ds-bg-secondary'>
        <ImageOff className='size-8 text-ds-fg-quaternary' strokeWidth={1.5} />
      </div>
    )
  }

  return (
    <img
      src={repairImageKitUrl(product.image)}
      alt={product.name}
      className='h-full w-full object-contain p-5 transition-transform duration-300 group-hover:scale-[1.04]'
      loading='lazy'
      onError={() => setErrored(true)}
    />
  )
}

function PickRow({
  pick,
  product,
  sourceReport,
  position,
}: {
  pick: HubTopPicksPick
  product: HubProductRef
  sourceReport: HubReportRef | null
  position: number
}) {
  const hubAnalytics = useHubAnalytics()
  const firstBuyLink = product.buyLinks[0]
  const priceLabel =
    product.priceLabel ??
    (firstBuyLink?.price != null
      ? formatPrice(firstBuyLink.price, firstBuyLink.currency)
      : null)

  const handleReportClick = (): void => {
    if (!sourceReport) return

    hubAnalytics?.onReportClick({
      reportId: sourceReport.reportId,
      reportSlug: sourceReport.slug,
      reportTitle: sourceReport.title,
      position,
    })
  }

  return (
    <li
      className={cn(
        'group relative flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-white transition-[border-color,box-shadow] duration-300 hover:border-foreground-secondary hover:shadow-sm',
        sourceReport && 'cursor-pointer'
      )}
    >
      {sourceReport && (
        <Link
          href={sourceReport.href}
          aria-label={`Read report: ${sourceReport.title}`}
          className='absolute inset-0 z-10 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
          onClick={handleReportClick}
        />
      )}

      <div className='relative aspect-[4/3] w-full overflow-hidden bg-ds-bg-secondary'>
        <PickThumb product={product} />
        <span className='absolute left-3 top-3 rounded-full bg-foreground px-2.5 py-1 text-xs font-medium text-background'>
          {pick.label}
        </span>
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-3 p-4'>
        <h3 className='text-balance font-display text-lg font-semibold leading-6 text-foreground'>
          {product.name}
        </h3>
        <p className='line-clamp-3 text-sm leading-5 text-foreground-secondary'>
          {renderRichText(pick.reason)}
        </p>

        {(priceLabel || firstBuyLink || sourceReport) && (
          <div className='mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1'>
            {priceLabel && (
              <span className='text-sm font-semibold text-foreground'>
                {priceLabel}
              </span>
            )}
            {firstBuyLink && (
              <a
                href={toBuyLinkHref(firstBuyLink.url, 'seo-hub')}
                target='_blank'
                rel='sponsored nofollow noopener noreferrer'
                className='relative z-20 inline-flex items-center gap-1 text-sm font-semibold text-info-700 hover:underline'
                onClick={() =>
                  hubAnalytics?.onProductClick({
                    productId: product.productId,
                    productSlug: product.slug,
                    productName: product.name,
                    outboundUrl: firstBuyLink.url,
                    price: firstBuyLink.price ?? null,
                    position,
                    sectionType: 'topPicks',
                  })
                }
              >
                Where to buy
                <ArrowRight className='size-3.5' aria-hidden />
              </a>
            )}
            {sourceReport && (
              <span className='inline-flex items-center gap-1 text-sm font-semibold text-info-700 group-hover:underline group-focus-within:underline'>
                Read report
                <ArrowRight className='size-3.5' aria-hidden />
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

export function HubTopPicks({
  section,
  context,
}: HubSectionComponentProps<HubTopPicksSection>) {
  const resolved: Array<{
    pick: HubTopPicksPick
    product: HubProductRef
    sourceReport: HubReportRef | null
  }> = []
  for (const pick of section.picks) {
    const product = context.productsById[pick.productId]
    if (!product) continue
    const sourceReport = pick.sourceReportId
      ? (context.reportsById[pick.sourceReportId] ?? null)
      : null
    resolved.push({ pick, product, sourceReport })
  }

  if (resolved.length === 0) return null

  return (
    <section id={section.id} className='flex flex-col gap-5'>
      {section.title && (
        <h2 className='font-display text-2xl font-bold leading-8 text-foreground'>
          {section.title}
        </h2>
      )}
      {section.dek && (
        <p className='text-base leading-6 text-foreground-secondary'>
          {section.dek}
        </p>
      )}

      <ul className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
        {resolved.map(({ pick, product, sourceReport }, index) => (
          <PickRow
            key={pick.productId}
            pick={pick}
            product={product}
            sourceReport={sourceReport}
            position={index}
          />
        ))}
      </ul>

      {section.moreLink && (
        <Link
          href={section.moreLink.href}
          className='text-sm font-medium text-foreground-secondary hover:text-foreground'
        >
          {section.moreLink.label}
        </Link>
      )}
    </section>
  )
}
